// WhatsApp QR — "Editar" y "Eliminar para todos" (spec /root/docs/whatsapp-qr-edit-revoke-design.md §4.6). Réplica PURA de
// la regla del backend (Masifi::WhatsappQr::Outbound::MessageAction) SOLO para mostrar u ocultar las acciones del menú: el
// backend decide siempre. Ventanas, tope de texto y claves sintéticas salen de la MISMA fuente que el backend (plugin YAML
// de Vite, como helper/markdownEmbeds.js).
import config from '../../../../../config/masifi/whatsapp_qr_message_actions.yml';
import { WHATSAPP_QR_FEATURE, isWhatsappQrInbox } from './masifiWhatsappQr';
import { isMasifiWaGroupChat } from './masifiWaGroups';

export const MASIFI_WA_EDIT_FEATURE = config.features.edit;
export const MASIFI_WA_DELETE_FEATURE = config.features.revoke;
// Reaccionar (spec polish-1 §3.7): clave sintética y reacciones rápidas de la MISMA fuente que el backend.
export const MASIFI_WA_REACT_FEATURE = config.features.react;
export const MASIFI_WA_QUICK_REACTIONS = config.reactions;
export const MASIFI_WA_WINDOWS = {
  edit: Number(config.edit_seconds),
  revoke: Number(config.revoke_seconds),
};
export const MASIFI_WA_MAX_TEXT = Number(config.max_text_length);

const OUTGOING = 1;
const EDITABLE_MEDIA = ['image', 'video', 'file'];
const MENTION = /@\S/;
const attributesOf = message => message?.content_attributes || {};

// Fuente única del "cuándo se envió". Espeja al backend: min(external_created_at, created_at), de modo que un valor futuro
// o en milisegundos nunca mantiene la ventana abierta.
export const masifiWaSentAt = message => {
  const created = Number(message?.created_at || 0);
  const external = Number(attributesOf(message).external_created_at || 0);
  return external ? Math.min(external, created) : created;
};

// Jerarquía nativa de Custom Roles (Masifi::ConversationAccess): admin, agente sin rol o conversation_manage = todo.
export const hasFullConversationAccess = (permissions = []) =>
  permissions.includes('administrator') ||
  permissions.includes('conversation_manage') ||
  !permissions.includes('custom_role');

// De NUESTRO número: saliente, no nota, no borrado, ya en WhatsApp, ni eco de un compañero en un grupo ni una reacción.
const isOwnNumberSent = message => {
  const attributes = attributesOf(message);
  return (
    message?.message_type === OUTGOING &&
    !message.private &&
    !attributes.deleted &&
    !['failed', 'progress'].includes(message.status) &&
    !!message.source_id &&
    !attributes.masifi_wa_author &&
    !attributes.masifi_wa_reaction
  );
};

const isAllowedFor = (message, permissions, userId) =>
  hasFullConversationAccess(permissions) ||
  (message.sender?.type === 'user' && message.sender?.id === userId);

const passesCommonRule = (context, feature, windowSeconds) => {
  const { message, inbox, features, permissions, userId, nowSeconds } = context;
  return (
    isWhatsappQrInbox(inbox) &&
    !!features?.[feature] &&
    isOwnNumberSent(message) &&
    isAllowedFor(message, permissions, userId) &&
    nowSeconds - masifiWaSentAt(message) < windowSeconds
  );
};

export const canDeleteForEveryone = context =>
  passesCommonRule(context, MASIFI_WA_DELETE_FEATURE, MASIFI_WA_WINDOWS.revoke);

// El pie va en el 1.º adjunto: imagen/video/documento y no escrito en el teléfono (el REST no trae masifi_wa_parts; el
// backend sí lo mira y responde sent_from_phone).
const isEditableMedia = message => {
  const [first] = message.attachments || [];
  if (!first) return true;
  return (
    EDITABLE_MEDIA.includes(first.file_type) &&
    !attributesOf(message).external_echo
  );
};

export const canEdit = context => {
  const { message, conversation } = context;
  return (
    passesCommonRule(context, MASIFI_WA_EDIT_FEATURE, MASIFI_WA_WINDOWS.edit) &&
    message.content_type === 'text' &&
    isEditableMedia(message) &&
    !(isMasifiWaGroupChat(conversation) && MENTION.test(message.content || ''))
  );
};

const NOT_REACTABLE_TYPES = [2, 3]; // actividad, plantilla
// Reaccionar (spec polish-1 §3.7; réplica de MessageAction.react_block_reason SOLO para mostrar): cualquier mensaje del
// chat que ya esté en WhatsApp, entrante o saliente; no notas, actividad, plantillas, reacciones, eliminados ni fallidos.
// Sin permiso por rol: puede quien ve la conversación (lo decide el backend).
export const canReact = ({ message, inbox, features }) => {
  const attributes = attributesOf(message);
  return (
    isWhatsappQrInbox(inbox) &&
    !!features?.[MASIFI_WA_REACT_FEATURE] &&
    !!message?.source_id &&
    !message.private &&
    !NOT_REACTABLE_TYPES.includes(message.message_type) &&
    !attributes.deleted &&
    !attributes.masifi_wa_reaction &&
    !['failed', 'progress'].includes(message.status)
  );
};

// La reacción viva de NUESTRO número a ese mensaje (fila `me`: saliente, sin autor de grupo, no eliminada).
export const ownReactionFor = (message, messages = []) =>
  messages.find(item => {
    const attributes = attributesOf(item);
    return (
      item.message_type === OUTGOING &&
      !!attributes.masifi_wa_reaction &&
      !attributes.deleted &&
      !attributes.masifi_wa_author &&
      attributes.in_reply_to === message?.id
    );
  }) || null;

// "Ver original" de un mensaje eliminado (follow-up del PR #192): con el módulo WhatsApp QR activo en la cuenta
// (enabled_features; apagado el endpoint da 403) y para el administrador de la cuenta o el autor del mensaje (un contacto
// con el mismo id no es el agente). Solo muestra u oculta: el backend decide.
export const canViewMasifiWaDeletedOriginal = ({
  sender,
  currentUserId,
  role,
  features,
}) =>
  !!features?.[WHATSAPP_QR_FEATURE] &&
  (role === 'administrator' ||
    (sender?.type === 'user' && sender?.id === currentUserId));

// Qué decirle al agente cuando la acción no terminó (revisión final I-2). Solo NUESTRO formato (`{ error, code }`, ya
// traducido por el backend) se muestra tal cual: los textos fijos de upstream/Rails vienen en inglés. Sin él: 401/403/404 =
// ya no tiene acceso; 5xx, timeout o sin respuesta = puede haberse aplicado (Rack::Timeout corta con un 500 mientras el
// comando sigue en la cola del conector); el resto = el genérico. `texts` = { generic, noAccess, unconfirmed }.
const NO_ACCESS_STATUSES = [401, 403, 404];
export const masifiWaActionErrorMessage = (error, texts) => {
  const response = error?.response;
  if (response?.data?.code && response.data.error) return response.data.error;
  if (NO_ACCESS_STATUSES.includes(response?.status)) return texts.noAccess;
  if (!response || response.status >= 500) return texts.unconfirmed;
  return texts.generic;
};
